import React, { useEffect, useState, useRef, memo } from 'react';
import { Button, Container, Row, Col, Card } from 'react-bootstrap';
import {
  getNotificationPreferences,
  setNotificationPreferences,
} from '../../services';
import { Formik, Field, FieldArray, Form as FormikForm } from 'formik';
import Loading from '../DocList/Loading';
import { toast } from 'react-toastify';
import { BsInfoCircleFill } from "react-icons/bs";
import BasicTooltip from '../Tooltip/BasicTooltip';
import { useSelector } from 'react-redux';
import {
  selectAuthToken,
  selectAuthUser,
  selectAuthProfiles,
} from '../../redux/selectors/authSelectors';

// Global cache to prevent multiple API calls across component instances
const preferencesCache = {
  data: null,
  timestamp: 0,
  isFetching: false,
  fetchPromise: null,
};

const CACHE_DURATION = 5 * 1000; // 5 seconds (very short cache for fresh data)

// Function to clear the cache
const clearPreferencesCache = () => {
  preferencesCache.data = null;
  preferencesCache.timestamp = 0;
  preferencesCache.isFetching = false;
  preferencesCache.fetchPromise = null;
};

const NotificationIntegration = memo(() => {
  const token = useSelector(selectAuthToken);
  const user = useSelector(selectAuthUser);
  const profiles = useSelector(selectAuthProfiles);

  const [preferences, setPreference] = useState(null);
  const isMountedRef = useRef(true);
  const DAYS = [
    'SUNDAY',
    'MONDAY',
    'TUESDAY',
    'WEDNESDAY',
    'THURSDAY',
    'FRIDAY',
    'SATURDAY',
  ];
  const NOTIFICATION_TYPES = [
    { title: 'AI Recommendations', key: 'NEW_RECOMMENDATION' },
    { title: 'Document Updates', key: 'DOCUMENT_UPDATED' },
    { title: 'Watch List Additions', key: 'WATCH_LIST_ADD' },
  ];
  const NOTIFICATION_TYPE_KEYS = NOTIFICATION_TYPES.map((t) => t.key);

  // Clear cache on component mount to ensure fresh data
  useEffect(() => {
    clearPreferencesCache();
  }, []);

  // Force refresh data every time the component mounts (page opened)
  useEffect(() => {
    // Clear cache and force fresh data fetch
    clearPreferencesCache();
    setPreference(null); // Reset preferences to trigger fresh fetch
  }, []);

  // Clear cache every time the component becomes visible (page opened)
  useEffect(() => {
    const handleFocus = () => {
      clearPreferencesCache();
    };

    window.addEventListener('focus', handleFocus);

    return () => {
      window.removeEventListener('focus', handleFocus);
    };
  }, []);

  // Clear cache when component unmounts
  useEffect(() => {
    return () => {
      clearPreferencesCache();
    };
  }, []);

  // Clear cache when page becomes visible again (user navigated back)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        clearPreferencesCache();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  useEffect(() => {
    if (token && user?.profiles?.length > 0) {
      const now = Date.now();
      const repositoryId = user.profiles[0].repositoryId;

      // Clear cache if user or token changed (navigation detected)
      if (
        preferencesCache.data &&
        (preferencesCache.lastUserId !== user.id ||
          preferencesCache.lastToken !== token)
      ) {
        clearPreferencesCache();
      }

      // Store current user and token for comparison
      preferencesCache.lastUserId = user.id;
      preferencesCache.lastToken = token;

      if (
        preferencesCache.data &&
        now - preferencesCache.timestamp < CACHE_DURATION
      ) {
        if (isMountedRef.current) {
          setPreference(preferencesCache.data);
        }
        return;
      }

      // If we have cached data but it's expired, use it while fetching fresh data
      if (preferencesCache.data && isMountedRef.current) {
        setPreference(preferencesCache.data);
      }

      // Check if we're already fetching
      if (preferencesCache.isFetching && preferencesCache.fetchPromise) {
        preferencesCache.fetchPromise.then((res) => {
          if (isMountedRef.current) {
            setPreference(res);
          }
        });
        return;
      }

      // Start fetching
      preferencesCache.isFetching = true;

      preferencesCache.fetchPromise = getNotificationPreferences({
        repositoryId,
      })
        .then((res) => {
          preferencesCache.isFetching = false;
          preferencesCache.fetchPromise = null;
          if (res) {
            preferencesCache.data = res;
            preferencesCache.timestamp = now;

            if (isMountedRef.current) {
              setPreference(res);
            }
          }
          return res;
        })
        .catch((error) => {
          preferencesCache.isFetching = false;
          preferencesCache.fetchPromise = null;

          if (isMountedRef.current) {
            // Set a default structure to prevent infinite loading
            const defaultPrefs = {
              daysOfWeek: [],
              numNotificationsPerDay: 1,
              profilePreferences: [],
            };
            preferencesCache.data = defaultPrefs;
            preferencesCache.timestamp = now;
            setPreference(defaultPrefs);
          }
          return null;
        });
    }

    // Cleanup function to prevent state updates on unmounted component
    return () => {
      isMountedRef.current = false;
    };
  }, [token, user]);

  const submit = async (values) => {
    const request = {
      daysOfWeek: values.daysOfWeek,
      numNotificationsPerDay: values.numNotificationsPerDay,
      timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      profilePreferences: values.profilePreferences,
    };

    const repositoryId = user?.profiles?.[0]?.repositoryId;

    setNotificationPreferences(request, { repositoryId })
      .then((res) => {
        toast.success('Notification preferences saved successfully!', {
          position: 'top-right',
          autoClose: 4000,
        });
      })
      .catch((error) => {
        console.error('Failed to save notification preferences:', error);
        toast.error('Failed to save notification preferences', {
          position: 'top-right',
          autoClose: 4000,
        });
      });
  };

  if (!preferences) {
    return (
      <Container>
        <Row>
          <Col className='d-flex justify-content-center'>
            <Loading />
          </Col>
        </Row>
      </Container>
    );
  }

  const mapProfilePreferencesToCategories = (
    profilePreferences,
    userProfiles
  ) => {
    // Add null/undefined checks
    if (!profilePreferences || !Array.isArray(profilePreferences)) {
      return {
        general_security: [],
        it_environment: [],
        cyber_tools: [],
      };
    }

    if (!userProfiles || !Array.isArray(userProfiles)) {
      return {
        general_security: [],
        it_environment: [],
        cyber_tools: [],
      };
    }

    // Create a map of profileId to profileName for quick lookup
    const profileMap = {};
    userProfiles.forEach((profile) => {
      profileMap[profile.profileId] = profile.profileName;
    });

    // Initialize the three categories
    const categories = {
      general_security: [],
      it_environment: [],
      cyber_tools: [],
    };

    // Map each profile preference to the appropriate category
    profilePreferences.forEach((preference) => {
      const profileName = profileMap[preference.profileId];

      if (!profileName || typeof profileName !== 'string') {
        return; // Skip if profile not found or not a string
      }

      // Categorize based on profile name
      if (
        profileName === 'Cybersecurity News' ||
        profileName === 'NIST NVD Postings' ||
        profileName === 'Cybersecurity Podcasts' ||
        profileName === 'MITRE ATT&CK' ||
        profileName.includes('MITRE')
      ) {
        categories.general_security.push(preference);
      } else if (
        profileName.includes('Stack News') ||
        profileName.includes('Stack NIST') ||
        (profileName.includes('Podcasts') && profileName.includes('Stack'))
      ) {
        categories.it_environment.push(preference);
      } else if (
        profileName.includes('Toolbox News') ||
        profileName.includes('Toolbox NIST') ||
        (profileName.includes('Podcasts') && profileName.includes('Tools'))
      ) {
        categories.cyber_tools.push(preference);
      }
    });

    return categories;
  };

  // Only call the function if we have valid data
  const categorizedPreferences =
    preferences && user?.profiles
      ? mapProfilePreferencesToCategories(
        preferences.profilePreferences,
        user.profiles
      )
      : {
        general_security: [],
        it_environment: [],
        cyber_tools: [],
      };

  return (
    <Container>
      <Row>
        <Col lg={12}>
          <div className='mt-4 '>
            <Card>
              <Card.Header as='h5' className='cstm-card-title'>
                Automated Alerts Set Up
              </Card.Header>
              <Card.Body>
                <Formik initialValues={preferences} onSubmit={submit}>
                  {({ values, setFieldValue }) => {
                    const getProfileTitle = (profileId) => {
                      const profileKey =
                        Object.entries(profiles || {}).find(
                          ([, p]) => p.profileId === profileId
                        )?.[0] || '';

                      if (profileKey.includes('podcasts'))
                        return 'Cybersecurity Podcast';
                      if (profileKey.includes('mitre')) return 'MITRE ATT&CK';
                      if (profileKey.includes('cve')) return 'NIST CVEs';
                      return 'Cybersecurity News';
                    };

                    const ProfileNotificationTypes = ({
                      profileId,
                      profileIndex,
                      testIdPrefix,
                    }) => {
                      if (profileIndex < 0) return null;

                      const fieldName = `profilePreferences.${profileIndex}.notificationTypes`;
                      const selected =
                        values.profilePreferences?.[profileIndex]
                          ?.notificationTypes || [];
                      const isAllSelected = NOTIFICATION_TYPE_KEYS.every((k) =>
                        selected.includes(k)
                      );

                      return (
                        <>
                          <label>
                            <input
                              type='checkbox'
                              checked={isAllSelected}
                              onChange={(e) => {
                                const checked = e.target.checked;
                                setFieldValue(
                                  fieldName,
                                  checked ? NOTIFICATION_TYPE_KEYS : []
                                );
                              }}
                              data-testid={`${testIdPrefix}-${profileId}`}
                            />
                            &nbsp;Select all&nbsp;&nbsp;&nbsp;&nbsp;
                          </label>
                          {NOTIFICATION_TYPES?.map((type) => (
                            <label key={`${profileId}-${type.key}`}>
                              <Field
                                type='checkbox'
                                name={fieldName}
                                value={type.key}
                                checked={selected.includes(type.key)}
                                data-testid={`alert-setup-checkbox-${type.key}`}
                              />
                              &nbsp;
                              {type.title}
                              &nbsp;&nbsp;&nbsp;&nbsp;
                            </label>
                          ))}
                        </>
                      );
                    };

                    const renderProfileList = (profileList, testIdPrefix) =>
                      (profileList || []).map((profile, i) => {
                        const profileIndex =
                          values.profilePreferences?.findIndex(
                            (p) => p.profileId === profile.profileId
                          ) ?? -1;

                        return (
                          <span key={profile.profileId}>
                            <Card.Title>
                              {getProfileTitle(profile.profileId)}
                            </Card.Title>
                            <ProfileNotificationTypes
                              profileId={profile.profileId}
                              profileIndex={profileIndex}
                              testIdPrefix={testIdPrefix}
                            />
                            {i < profileList.length - 1 && <hr />}
                          </span>
                        );
                      });

                    return (
                      <FormikForm>
                      <Card className='mb-3'>
                        <Card.Header as='h5' className='cstm-card-title'>
                          General Security Intelligence
                        </Card.Header>
                        <Card.Body>
                          <FieldArray name='profilePreferences'>
                            {() => (
                              <>
                                {renderProfileList(
                                  categorizedPreferences.general_security,
                                  'alert-setup-select-all-general-security'
                                )}
                              </>
                            )}
                          </FieldArray>
                        </Card.Body>
                      </Card>

                      <Card className='mb-3'>
                        <Card.Header as='h5' className='cstm-card-title'>
                          IT Environment Feed
                        </Card.Header>
                        <Card.Body>
                          <FieldArray name='profilePreferences'>
                            {() => (
                              <>
                                {renderProfileList(
                                  categorizedPreferences.it_environment,
                                  'alert-setup-select-all-it-environment'
                                )}
                              </>
                            )}
                          </FieldArray>
                        </Card.Body>
                      </Card>

                      <Card className='mb-3'>
                        <Card.Header as='h5' className='cstm-card-title'>
                          Cyber Tools Feed
                        </Card.Header>
                        <Card.Body>
                          <FieldArray name='profilePreferences'>
                            {() => (
                              <>
                                {renderProfileList(
                                  categorizedPreferences.cyber_tools,
                                  'alert-setup-select-all-cyber-tools'
                                )}
                              </>
                            )}
                          </FieldArray>
                        </Card.Body>
                      </Card>

                      <Card className='mb-3'>
                        <Card.Body>
                          <Card.Title>Days to Receive Notifications</Card.Title>
                          <FieldArray name='daysOfWeek'>
                            {() => (
                              <>
                                <label key='days-select-all'>
                                  <input
                                    type='checkbox'
                                    checked={DAYS.every((d) =>
                                      values.daysOfWeek.includes(d)
                                    )}
                                    onChange={(e) => {
                                      const checked = e.target.checked;
                                      setFieldValue(
                                        'daysOfWeek',
                                        checked ? DAYS : []
                                      );
                                    }}
                                    data-testid='alert-setup-select-all-days'
                                  />
                                  &nbsp;Select all&nbsp;&nbsp;&nbsp;&nbsp;
                                </label>
                                {DAYS.map((day, index) => (
                                  <label key={day}>
                                    <Field
                                      type='checkbox'
                                      name={`daysOfWeek`}
                                      value={day}
                                      checked={values.daysOfWeek.includes(day)}
                                    />
                                    &nbsp;
                                    {day}&nbsp;&nbsp;&nbsp;&nbsp;
                                  </label>
                                ))}
                              </>
                            )}
                          </FieldArray>
                        </Card.Body>
                      </Card>

                      <Card className='mb-3'>
                        <Card.Body>
                          <div className="d-flex align-items-center">
                            <Card.Title className="mb-0">Times Per Day</Card.Title>
                            <BasicTooltip text={`Set how many notifications you want to receive per day. You can enter any value from 1 to 4.`}>
                              <div style={{ marginLeft: '4px', cursor:'pointer' }}><BsInfoCircleFill /></div>
                            </BasicTooltip>
                          </div>
                          <label className="mt-2 w-25">
                            <Field
                              type='number'
                              min={1}
                              max={4}
                              className="w-50"
                              value={values.numNotificationsPerDay === "" ? "" : values.numNotificationsPerDay}
                              onChange={(e) => {
                                let val = e.target.value;
                                if (val === "") {
                                  setFieldValue("numNotificationsPerDay", "");
                                  return;
                                }
                                // allow only digits
                                if (!/^[0-9]+$/.test(val)) return;
                            
                                const num = Number(val);
                            
                                // hard block <1 or >4 WHILE TYPING
                                if (num < 1 || num > 4) return;
                            
                                setFieldValue("numNotificationsPerDay", num);
                              }}
                              onBlur={(e) => {
                                let val = e.target.value;
                            
                                if (val === "" || Number(val) < 1) {
                                  setFieldValue("numNotificationsPerDay", 1);
                                  return;
                                }
                            
                                if (Number(val) > 4) {
                                  setFieldValue("numNotificationsPerDay", 4);
                                }
                              }}
                              name={'numNotificationsPerDay'}
                            />
                            &nbsp;
                          </label>
                        </Card.Body>
                      </Card>
                      <Button type='submit' variant='primary'>
                        Save Preferences
                      </Button>
                    </FormikForm>
                    );
                  }}
                </Formik>
              </Card.Body>
            </Card>
          </div>
        </Col>
      </Row>
    </Container>
  );
});

NotificationIntegration.displayName = 'NotificationIntegration';

export default NotificationIntegration;
